import React, {useEffect, useState} from 'react';
import {
  IFarmInputQuote,
  IRouteScreenProps,
  ISupplierUserProfile,
  IUser,
  QuotationStatus,
} from 'constants/interfaces';
import {apiGetDepotQuotes, apiGetUser} from 'constants/Api';
import {
  Box,
  Button,
  ButtonGroup,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {Route, useLocation} from 'react-router-dom';
import AddIcon from '@material-ui/icons/Add';
import NavigationService from 'services/NavigationService';
import {
  ActiveFilterState,
  KhulaSupplierTable,
  Row,
} from 'components/Table/KhulaSupplierTable';
import {
  getDefaultStateFilter,
  quoteTableFilters,
  quoteTableHeaders,
  quoteTableRows,
} from 'screens/Dashboard/Quotes/QuoteTableSetup';
import {connect} from 'react-redux';
import {QuoteState} from 'appredux/reducers/reducer-quotes';
import moment from 'moment';
import {reduxSetQuotes} from 'appredux/actions';
import {PermissionsService} from 'services/PermissionsService';
import {ESupplierUserPermissions} from 'constants/index';

type QuotesProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
  quoteState: QuoteState;
};

/**
 * Component displaying quotes screen
 */
class Quotes extends React.Component<QuotesProps> {
  static readonly MAX_QUOTES_LIMIT = 500;

  constructor(props: QuotesProps) {
    super(props);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
  }

  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    fetchSize?: number;
    user?: IUser;
    farmerId?: string;
    filteredQuotes?: IFarmInputQuote[];
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    fetchSize: 20,
    user: undefined,
    farmerId: undefined,
    filteredQuotes: undefined,
  };

  componentDidMount() {
    this.loadUserAndQuotes();
  }

  componentDidUpdate(prevProps: QuotesProps) {
    // Reload if userId query param changes
    const prevUserId = new URLSearchParams(prevProps.location.search).get(
      'userId',
    );
    const currentUserId = new URLSearchParams(this.props.location.search).get(
      'userId',
    );

    if (prevUserId !== currentUserId) {
      this.loadUserAndQuotes();
    }
  }

  loadUserAndQuotes() {
    const {quoteState} = this.props;
    const noQuotes = !quoteState.quotes?.length;
    const queryParams = new URLSearchParams(this.props.location.search);
    const userId = queryParams.get('userId');

    Promise.resolve()
      .then(() => {
        if (userId)
          return apiGetUser(userId).catch((err) => {
            console.error('Failed to fetch user:', err);
          });
      })
      .then((user) => {
        this.setState(
          {
            isLoading: noQuotes,
            user,
            farmerId: user?.farmer?.id,
            filteredQuotes: undefined,
          },
          () => this.loadQuotes(),
        );
      });
  }

  loadQuotes() {
    const {quoteState} = this.props;
    const {lastUpdate, quotes} = quoteState;

    const quotesAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(30, 'minutes'),
    );
    const noQuotes = !quotes?.length;

    if (quotesAreExpired || noQuotes || this.state.farmerId)
      this.refreshQuotes();
  }

  refreshQuotes() {
    const {fetchSize, farmerId} = this.state;
    const apiLimit =
      typeof fetchSize === 'number' && fetchSize > 0
        ? fetchSize
        : Quotes.MAX_QUOTES_LIMIT;

    this.setState({isRefreshing: true}, () => {
      apiGetDepotQuotes(apiLimit, farmerId)
        .then((quotes: IFarmInputQuote[]) => {
          if (!farmerId) {
            reduxSetQuotes(quotes);
          } else {
            // Store filtered quotes in component state when filtering by farmer
            this.setState({filteredQuotes: quotes});
          }
          this.setState({showRefreshedSnackbar: true});
        })
        .catch((err) => {
          alert('Failed to load quotes. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false, isRefreshing: false}));
    });
  }

  openCreateQuote() {
    NavigationService.navigate('create-quote');
  }

  /**
   * Update the number of quotes to fetch.
   * @param {number | undefined} size Number of records to fetch; undefined fetches all.
   */
  handleFetchSizeUpdate(size?: number) {
    this.setState({fetchSize: size}, () => {
      this.refreshQuotes();
    });
  }

  handleRowsPerPageChange(size: number) {
    const normalizedSize = size === -1 ? undefined : size;
    this.handleFetchSizeUpdate(normalizedSize);
  }

  render() {
    const {quoteState} = this.props;
    const {lastUpdate, quotes: reduxQuotes} = quoteState;
    const {
      isLoading,
      isRefreshing,
      showRefreshedSnackbar,
      fetchSize,
      user,
      farmerId,
      filteredQuotes,
    } = this.state;

    // Use filtered quotes when filtering by farmer, otherwise use Redux quotes
    const quotes = farmerId && filteredQuotes ? filteredQuotes : reduxQuotes;

    const isAdmin = this.props.user.isAdmin;

    const permissions = new PermissionsService(this.props.user);
    const canWrite = permissions.hasPermission(
      ESupplierUserPermissions.QUOTE_WRITE,
    );

    const userFullName = user ? user?.name + ' ' + user?.surname : '';
    const toolbarActions = (
      <>
        <ButtonGroup variant="text">
          {[20, 50, undefined].map((size, index) => (
            <Button
              key={index}
              variant={fetchSize === size ? 'contained' : 'text'}
              disabled={isLoading || isRefreshing}
              disableElevation
              onClick={() => this.handleFetchSizeUpdate(size)}
              size={'small'}
            >
              {size || 'All'}
            </Button>
          ))}
        </ButtonGroup>
        {canWrite && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => this.openCreateQuote()}
            size="small"
            className="teams-btn"
            color={'primary'}
          >
            New Quote
          </Button>
        )}
      </>
    );

    return (
      <div className="table-screen quotes-container">
        <Typography variant="h4" className="page-title">
          Quotes{userFullName ? ': ' + userFullName : ''}
        </Typography>
        <Typography variant="body1" className="page-subtitle">
          Your depot quotes
        </Typography>
        <Route
          render={() => (
            <div className="quotes-section">
              <QuotesTable
                isAdmin={isAdmin}
                quotes={quotes}
                isLoading={isLoading}
                isRefreshing={isRefreshing}
                onRefresh={() => this.refreshQuotes()}
                fetchSize={fetchSize}
                onRowsPerPageChange={this.handleRowsPerPageChange}
                toolbarActions={toolbarActions}
              />
              <Box mt={1}>
                <Typography variant={'caption'}>
                  Last updated: {moment(lastUpdate).fromNow()}
                </Typography>
              </Box>
              <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                open={showRefreshedSnackbar}
                onClose={() => this.setState({showRefreshedSnackbar: false})}
                autoHideDuration={3000}
                message="Quotes refreshed"
              />
            </div>
          )}
        />
      </div>
    );
  }
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const QuotesTable = (props: {
  isAdmin: boolean;
  quotes: IFarmInputQuote[];
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: VoidFunction;
  fetchSize?: number;
  onRowsPerPageChange?: (size: number) => void;
  toolbarActions?: React.ReactNode;
}) => {
  const {
    quotes,
    isAdmin,
    isLoading,
    isRefreshing,
    onRefresh,
    fetchSize,
    onRowsPerPageChange,
    toolbarActions,
  } = props;

  const query = useQuery();
  const [defaultActiveFilters, setDefaultActiveFilters] = useState<
    ActiveFilterState[]
  >([]);

  useEffect(() => {
    const activeFilters = [];
    const state = query.get('state');

    if (state) {
      activeFilters.push(getDefaultStateFilter(state as QuotationStatus));
    }

    setDefaultActiveFilters(activeFilters);
  }, [query?.get('state')]);

  const mapRowToQuote = (row: Row) =>
    // @ts-ignore
    quotes.find((quote) => quote.id == row.id);

  return (
    <KhulaSupplierTable
      headers={quoteTableHeaders(isAdmin)}
      rows={quoteTableRows(quotes, (quote) => {
        NavigationService.navigate(`/quotes/${quote.id}`);
      })}
      filtersGroups={quoteTableFilters}
      defaultActiveFilters={defaultActiveFilters}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      defaultRowsPerPage={typeof fetchSize === 'number' ? fetchSize : -1}
      rowsPerPageOptions={[20, 50, {label: 'All', value: -1}]}
      onRowsPerPageChange={onRowsPerPageChange}
      toolbarActions={toolbarActions}
      onRowSelect={(row) => {
        const quote = mapRowToQuote(row);
        if (!quote) return;
        NavigationService.navigate(`/quotes/${quote.id}`);
      }}
    />
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    quoteState: state.quotes,
  };
};
export default connect(mapStateToProps)(Quotes);
